/**
 * useContextLifecycle — gère save/restore par contexte au changement de route.
 *
 * @remarks
 * Logique IDE-like :
 * - **Première visite d'un contexte** : applique les `panelRequirements` du
 *   manifest (panneaux ouverts/fermés par défaut, tab actif par défaut).
 * - **Visites suivantes** : restaure l'état utilisateur (drag-and-drop,
 *   toggles, etc.) sauvegardé au précédent passage, puis rouvre les panneaux
 *   marqués `reopenOnVisit`.
 * - **Avant de quitter** : sauvegarde l'état courant pour le restaurer plus tard.
 *
 * Extrait de l'ancien `MainLayout.tsx` (~70 LOC qui squattaient le shell)
 * vers ce hook dédié, plus testable et plus lisible.
 */

import { useEffect, useRef } from 'react';
import { featureRegistry, type Context, type AppContext } from '@/app/registry';
import { useLayoutStore } from '@/app/stores/use-layout-store';

export function useContextLifecycle(currentContext: Context, appCtx: AppContext): void {
  const previousContextRef = useRef<Context>(currentContext);
  // Dernier contexte où l'on est *entré* : `null` au montage, pour que le
  // chargement direct d'une page compte comme une arrivée. Distingue une
  // arrivée d'un simple re-run (changement d'`appCtx`), qui ne doit pas
  // rouvrir un panneau que l'utilisateur vient de fermer.
  const enteredContextRef = useRef<Context | null>(null);

  const saveContextState = useLayoutStore((s) => s.saveContextState);
  const restoreContextState = useLayoutStore((s) => s.restoreContextState);
  const hasVisitedContext = useLayoutStore((s) => s.hasVisitedContext);
  const setLeftDrawerOpen = useLayoutStore((s) => s.setLeftDrawerOpen);
  const setRightDrawerOpen = useLayoutStore((s) => s.setRightDrawerOpen);
  const setLeftActiveTab = useLayoutStore((s) => s.setLeftActiveTab);
  const setRightActiveTab = useLayoutStore((s) => s.setRightActiveTab);

  useEffect(() => {
    const previousContext = previousContextRef.current;

    if (previousContext !== currentContext) {
      saveContextState(previousContext);
    }

    const isFirstVisit = !hasVisitedContext(currentContext);
    const setters = { setLeftDrawerOpen, setRightDrawerOpen, setLeftActiveTab, setRightActiveTab };

    if (isFirstVisit) {
      // Applique les panelRequirements du manifest (premier passage)
      applyPanelRequirements(currentContext, appCtx, setters);
      // Sauvegarde cet état initial
      saveContextState(currentContext);
    } else {
      restoreContextState(currentContext);
      if (enteredContextRef.current !== currentContext) {
        applyPanelRequirements(currentContext, appCtx, setters, { reopenOnly: true });
      }
    }

    enteredContextRef.current = currentContext;
    previousContextRef.current = currentContext;
    // `appCtx` est désormais memoizé dans les 3 shells (useMemo sur [user, flags]) →
    // référence stable tant que user/flags ne changent pas. Sûr à inclure dans
    // les deps. Un changement user/flags déclenchera juste un restoreContextState,
    // ce qui est safe (idempotent, pas de save du previous puisque current === previous).
  }, [
    currentContext,
    appCtx,
    hasVisitedContext,
    restoreContextState,
    saveContextState,
    setLeftActiveTab,
    setLeftDrawerOpen,
    setRightActiveTab,
    setRightDrawerOpen,
  ]);
}

interface PanelSetters {
  setLeftDrawerOpen: (open: boolean) => void;
  setRightDrawerOpen: (open: boolean) => void;
  setLeftActiveTab: (id: string | null) => void;
  setRightActiveTab: (id: string | null) => void;
}

/**
 * @param options.reopenOnly - visite suivante : n'applique que les panneaux
 *   `reopenOnVisit` (ouverts), le reste garde l'état restauré.
 */
function applyPanelRequirements(
  ctx: Context,
  appCtx: AppContext,
  setters: PanelSetters,
  { reopenOnly = false }: { reopenOnly?: boolean } = {},
): void {
  const features = featureRegistry.getActiveFeatures(ctx, appCtx);

  // On prend le premier manifest qui définit des panelRequirements pour ce ctx
  for (const m of features) {
    const req = m.panelRequirements?.[ctx];
    if (!req) continue;

    if (reopenOnly) {
      if (req.left?.reopenOnVisit && req.left.open) {
        setters.setLeftDrawerOpen(true);
        if (req.left.defaultActiveTab !== undefined) {
          setters.setLeftActiveTab(req.left.defaultActiveTab);
        }
      }
      if (req.right?.reopenOnVisit && req.right.open) {
        setters.setRightDrawerOpen(true);
        if (req.right.defaultActiveTab !== undefined) {
          setters.setRightActiveTab(req.right.defaultActiveTab);
        }
      }
      return;
    }

    if (req.left) {
      setters.setLeftDrawerOpen(req.left.open);
      if (req.left.defaultActiveTab !== undefined) {
        setters.setLeftActiveTab(req.left.defaultActiveTab);
      }
    }
    if (req.right) {
      setters.setRightDrawerOpen(req.right.open);
      if (req.right.defaultActiveTab !== undefined) {
        setters.setRightActiveTab(req.right.defaultActiveTab);
      }
    }
    return; // premier manifest matchant gagne
  }
}
